import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Badge, Button, CounterBadge, makeStyles } from "@fluentui/react-components";
import { People24Filled } from "@fluentui/react-icons";
import FluentParticipantOverlay from "../room/FluentParticipantsOverlay";
import { useMicrophoneStatus } from "../room/useMicrophoneStatus";
import FluentSettingParticipantOverlay from "../room/FluentSettingParticipantOverlay";
import { useSocialContext } from "../contexts/SocialContext";
import { useCssBreakpoints } from "react-use-css-breakpoints";

const useStyles = makeStyles({
  button: {
    height: "40px",
    minWidth: "40px",
    backgroundColor: "rgba(22, 24, 30, 0.60)",
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "#fff",
      fill: "#fff"
    }
  },
  buttonActive: {
    backgroundColor: "#16181E",
    height: "40px",
    minWidth: "40px",
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "#fff",
      fill: "#fff"
    }
  }
});

export default function ToolbarSocialButton({
  scene,
  hubChannel,
  presences,
  mySessionId,
  isOpenParticipants,
  toggleParticipants,
  closeParticipants,
  people,
  agents
}) {
  const breakpoint = useCssBreakpoints();
  const { toggleMute } = useMicrophoneStatus(scene);
  const [selectedPerson, setSelectedPerson] = useState(null);

  const { onOpenSettings, setIsOpenSettings, isOpenSettings } = useSocialContext();

  const allPeopleExceptMe = people.filter(person => !person.isMe);

  const allMuted = people.every(person => person?.micPresence === undefined || person?.micPresence?.muted);
  const isAllHidden = allPeopleExceptMe.every(person => hubChannel.isHidden(person.id));
  const allHost = allPeopleExceptMe.filter(person => person?.roles?.signed_in).every(person => person?.roles?.owner);

  const { privateUnreadMessages } = useSocialContext();

  const selectPerson = person => {
    if (person) {
      const selectedPersonFromPeople = people.find(p => p.id === person.id);
      setSelectedPerson(selectedPersonFromPeople);
    } else {
      setSelectedPerson(null);
    }
    onOpenSettings(true);
  };

  useEffect(() => {
    if (selectedPerson) {
      const updatedPerson = people.find(p => p.id === selectedPerson.id);
      if (updatedPerson) {
        setSelectedPerson(updatedPerson);
      }
    }
  }, [people, selectedPerson]);

  const styles = useStyles();

  const onMute = useCallback(
    person => {
      if (person.isMe && !person.micPresence.muted) {
        toggleMute();
      }

      if (person.presence === "room" && person.permissions) {
        hubChannel.mute(person.id);
      }
    },
    [hubChannel, toggleMute]
  );

  const onMuteAll = useCallback(() => {
    for (const person of people) {
      if (person.isMe && !person.micPresence.muted) {
        toggleMute();
      } else if (person.presence === "room" && person.permissions && !person.roles.creator) {
        hubChannel.mute(person.id);
      }
    }
  }, [people, toggleMute, hubChannel]);

  const closeSettings = useCallback(() => {
    setIsOpenSettings(false);
  }, [setIsOpenSettings]);

  useEffect(() => {
    if (selectedPerson && !people.some(person => person.id === selectedPerson.id)) {
      closeSettings();
    }
  }, [selectedPerson, people, closeSettings]);

  return (
    <>
      <FluentParticipantOverlay
        people={people}
        isOpen={isOpenParticipants}
        scene={scene}
        hubChannel={hubChannel}
        presences={presences}
        mySessionId={mySessionId}
        setIsOpen={toggleParticipants}
        closeParticipants={closeParticipants}
        onMute={onMute}
        onMuteAll={onMuteAll}
        selectPerson={selectPerson}
        agents={agents}
      />

      <FluentSettingParticipantOverlay
        hubChannel={hubChannel}
        isOpen={isOpenSettings}
        onClose={closeSettings}
        scene={scene}
        person={selectedPerson}
        onMute={onMute}
        allMuted={allMuted}
        people={people}
        isAllHidden={isAllHidden}
        onMuteAll={onMuteAll}
        allHost={allHost}
        onChange={toggleParticipants}
      />

      <div style={{ position: "relative" }}>
        <Button
          appearance="subtle"
          onClick={() => toggleParticipants()}
          className={isOpenParticipants ? styles.buttonActive : styles.button}
          icon={<People24Filled style={{ width: "24px", height: "24px" }} />}
        />
        {/* Add the CounterBadge for participant count */}
        <CounterBadge count={people.length} size="small" style={{ position: "absolute", top: "-3px", right: "-3px" }} />
        {privateUnreadMessages != false && (
          <Badge size="small" style={{ position: "absolute", top: "-3px", right: "-3px" }} />
        )}
      </div>
    </>
  );
}

ToolbarSocialButton.propTypes = {
  scene: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired,
  presences: PropTypes.array,
  mySessionId: PropTypes.string.isRequired,
  isOpenParticipants: PropTypes.bool.isRequired,
  toggleParticipants: PropTypes.func.isRequired,
  closeParticipants: PropTypes.func.isRequired,
  people: PropTypes.array.isRequired,
  agents: PropTypes.array.isRequired
};
